// DisclosureToggle — client-side boolean toggle for UI panels (ts-9).
//
// Replaces server-side boolean toggles in appearance_live,
// profile_live, and repo_show_live that have zero DB side-effects:
//   - toggle_advanced (appearance_live)
//   - toggle_all_stories (profile_live)
//   - toggleFork (repo_show_live)
//   - toggleSidebar (repo_show_live)
//
// Markup convention:
//   Attach phx-hook="DisclosureToggle" to the trigger element.
//   Set data-disclosure-target="<panel-id>" on the trigger.
//   The target element: id="<panel-id>" with class="hidden" when closed.
//
// The hook element is the trigger; the target panel is a DIFFERENT
// element the server can patch independently, which used to strip the
// client-set `hidden` class mid-interaction. State lives in the
// shared per-panel store (./panel_state) and `_panelOpen` on the hook
// is a display mirror refreshed on every interaction.

import type { LiveViewHook } from "./types";
import { attachPanel, type SharedPanel } from "./panel_state";

export interface DisclosureToggleState {
  _onClick: () => void;
  _panelOpen: boolean;
  _panel: SharedPanel;
}

const applyOpen = (open: boolean, panel: HTMLElement): void => {
  panel.classList.toggle("hidden", !open);
};

const domMatches = (open: boolean, panel: HTMLElement): boolean =>
  panel.classList.contains("hidden") !== open;

export const DisclosureToggle = {
  mounted(this: LiveViewHook<never> & DisclosureToggleState) {
    const targetId = this.el.dataset["disclosureTarget"];
    const target = targetId ? document.getElementById(targetId) : null;
    this._panel = attachPanel(target, applyOpen, domMatches);
    this._panelOpen = this._panel.isOpen();

    const onClick = (): void => {
      this._panelOpen = this._panel.toggle();
    };
    this.el.addEventListener("click", onClick);
    this._onClick = onClick;
  },

  updated(this: LiveViewHook<never> & DisclosureToggleState) {
    this._panel.enforce();
    this._panelOpen = this._panel.isOpen();
  },

  destroyed(this: LiveViewHook<never> & DisclosureToggleState) {
    this.el.removeEventListener("click", this._onClick as EventListener);
    this._panel.disconnect();
  },
};
